import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Tooltip } from '@material-ui/core';
import { ModalWrapper } from 'components/Modals';
import TokenIcon from 'assets/images/token.svg';
import { RootStore } from 'redux/store';
import './EulaModal.scss';
import { LoaderButton } from 'components/LoaderButton';
import { acceptEula } from 'redux/thunks';
import { resetAcceptEulaSuccess } from 'redux/actions';
import ReactHtmlParser from 'react-html-parser';
import { useEasterEggFound } from 'helpers/easterEgghelper';
import EASTER_EGG_IDS from 'helpers/easterEggConstants';

interface EulaModalProps {
	open: boolean;
	openFromMenu: boolean;
	onCloseClicked?: () => void;
}

const originalParagraphs = [
	`The Tokens of Appreciation (ToA) platform is available for employees of ExxonMobil Corporation and its affiliates. By indicating your
acceptance, you agree to adhere to these Terms of Use (Terms), all relevant provisions of the 
<a target='_blank' href='https://it.na.xom.com/learning-support/tutorials-how-tos/security/user-roles-and-responsibilities/key-it-user-responsibilities'>Key IT User Responsibilities</a>, and all other
applicable corporate guidelines and policies. If any user violates these Terms, the user may be removed from the ToA platform and/or
discipline may be imposed, up to and including discharge.`,
	`ToA is intended to be a place for ExxonMobil personnel to show their appreciation for the things their colleagues do for them and for the
Company. If you accept these Terms of Use, you will be able to access the ToA platform which will enable you to give and receive tokens of
appreciation as well share a few kind words with your colleagues. When you are part of a team set up within the ToA platform, you may also
be able to view a leaderboard which shows those team members that have received the most tokens of appreciation over the pertinent period of
time. You are responsible for any material you place on ToA.`,
	`By accepting these Terms, you indicate that you understand the following: (a) registering for and participating on the ToA platform is
voluntary, (b) the ToA platform is just a fun way to encourage ExxonMobil personnel to appreciate each other, (c) other ExxonMobil personnel
can give you tokens of appreciation, (d) the ToA platform will publicly identify all individuals, by name and MySite picture, who give and
receive tokens of appreciation as well as any comments provided by the individual that gives the tokens of appreciation, and (e) your name
may be provided on leaderboard.`,
	`Post information that is consistent with ExxonMobil’s Policies and Guidelines, particularly, <a target='_blank' href='https://hr.na.xom.com/us/forms/wrkthr'>the Working Together: Harassment in the
Workplace Policy and Practices.</a>`,
	`Use common sense and good judgment when using or posting content to the ToA Platform.`,
	`Use common courtesy – avoid statements that would reflect negatively on you or your colleagues.`,
	`Post content that is inflammatory.`,
	`Post content that is political, opinionated or antagonistic.`,
	`Post content that is harassing, derogatory, attacking or bullying.`,
	`Post information that is Private, Proprietary or Restricted as defined by the <a target='_blank' href='https://ishareteam1.na.xom.com/sites/SSHE002/Security/SitePages/MPI.aspx'>MPI Guidelines.</a>`,
	`
	Post ExxonMobil’s business confidential, proprietary or trade secret information or information that is subject to confidentiality
	obligations to third parties or protected by legal privileges (such as attorney client communications).
	`,
	`
	Post anything that is inappropriate, such as sexually, racially, or otherwise derogatory or discriminatory as defined by <a target='_blank' href='https://hr.na.xom.com/us/benefits-policies/us_harassment'>
	Harassment in the Workplace Policy and Practices.</a>
	`,
	`All content which you post to ToA becomes the property of ExxonMobil. ExxonMobil may permanently or temporarily terminate, suspend, or
	otherwise refuse to permit your access to the ToA platform without notice and liability, if, in ExxonMobil’s sole determination, you violate
	any of the terms herein. If you cease to be an eligible member of the ToA platform, your access to all ToA content may be terminated. Once
	removed from the ToA platform, your content may remain on the platform.`,
	`These Terms are not intended to conflict with any applicable federal, state, or local laws concerning employees’ right to communicate with
	their co-workers about the terms and conditions of their employment. Please keep in mind that you are more likely to resolve work-related
	complaints by speaking directly with your co-workers. If you decide to post complaints or criticism, avoid using statements, photographs,
	video, or audio that reasonably could be viewed as malicious, obscene, threatening, or intimidating, that disparages customers, employees,
	or suppliers or that might constitute harassment. Remember it is inappropriate for an employee to engage in conduct that is in violation of
	ExxonMobil’s workplace policies against discrimination or harassment on the basis of age, race, religion, gender, ethnicity, nationality,
	disability, veteran status, or any other status protected by law or company policy.`,
	`Any personally identifiable information that you provide about yourself in your ToA profile is your choice and is not a requirement to use
	the ToA Platform.`,
	`Only use ExxonMobil company owned devices to access ToA. Do not use your personal computer or personal mobile device to access ToA.`,
	`It is possible that information previously posted to ToA will become subject to a Legal Hold and thus must be preserved in accordance with
	the provisions of Section 8 of the Records Management Guidelines. However, users should not use the ToA platform to post or comment on
	matters covered by a Legal Hold.`,
	`If you do not agree to these Terms of Use, there will be no consequence for not accepting or granting consent, except that you may not use
	or participate on the ToA platform.`,
];

const EulaModal: React.FC<EulaModalProps> = (props: EulaModalProps) => {
	const { open, openFromMenu, onCloseClicked } = props;
	const [clicked, setClicked] = useState(false);
	const eulaAccepting = useSelector((state: RootStore) => state.user.eulaAccepting);
	const eulaAcceptingSuccess = useSelector((state: RootStore) => state.user.eulaAcceptingSuccess);
	const dispatch = useDispatch();
	const [paragraphs, setParagraphs] = useState<any[]>(originalParagraphs);
	const easterEggFound = useEasterEggFound(EASTER_EGG_IDS.LAWYER_JARGON);

	useEffect(() => {
		if (eulaAcceptingSuccess) {
			dispatch(resetAcceptEulaSuccess());
		}
	}, [eulaAcceptingSuccess, dispatch]);

	const handleSend = () => {
		dispatch(acceptEula());
	};

	const iconClicked = () => {
		setClicked(!clicked);

		if (clicked) {
			setParagraphs(originalParagraphs);
		} else {
			const newParagraphs = paragraphs.map(paragraph => {
				return blahify(paragraph);
			});

			setParagraphs(newParagraphs);
			easterEggFound();
		}
	};

	const blahify = (paragraph: string) => {
		const replacementLetters = ['b', 'l', 'a', 'h'];
		const staticCharacters = {
			' ': '',
			',': '',
			'.': '',
			'(': '',
			')': '',
			':': '',
			';': '',
		};
		const specialCharacters = {
			'<': '',
			'>': '',
		};
		let openHtml = false;
		let newParagraph = '';
		let place = 0;

		for (let letter of paragraph) {
			if (letter in specialCharacters) {
				if (letter === '<') {
					openHtml = true;
				} else {
					openHtml = false;
				}
				continue;
			}

			if (openHtml) {
				continue;
			}

			if (letter in staticCharacters) {
				place = 0;
				newParagraph += letter;
			} else {
				if (place === 4) {
					place = 0;
				}

				if (letter === letter.toLocaleUpperCase()) {
					newParagraph += replacementLetters[place].toLocaleUpperCase();
				} else {
					newParagraph += replacementLetters[place];
				}
				place += 1;
			}
		}

		return newParagraph;
	};

	return (
		<ModalWrapper height='tall' open={open} hideBackdrop={!openFromMenu} allowClickOutsideToClose={openFromMenu} onCloseClicked={onCloseClicked}>
			<Grid container direction='column' alignItems='center' className='eula-modal'>
				<Tooltip title='🥚'>
					<img src={TokenIcon} onClick={iconClicked} className='eula-modal__token-icon' alt='toa-token' />
				</Tooltip>
				<div className='eula-modal__title'>Tokens of Appreciation - Terms of Use</div>
				<div className='eula-modal__paragraph'>{ReactHtmlParser(paragraphs[0])}</div>

				<div className='eula-modal__section-title'>Using the ToA Platform</div>

				<div className='eula-modal__paragraph'>{ReactHtmlParser(paragraphs[1])}</div>

				<div className='eula-modal__paragraph'>{ReactHtmlParser(paragraphs[2])}</div>

				<div className='eula-modal__paragraph'>Follow these Do’s and Do Not’s when using or posting content on the ToA Platform:</div>

				<div className='eula-modal__section-title'>Do:</div>
				<ul className='eula-modal__paragraph'>
					<li>{ReactHtmlParser(paragraphs[3])}</li>
					<li>{ReactHtmlParser(paragraphs[4])} </li>
					<li>{ReactHtmlParser(paragraphs[5])}</li>
				</ul>

				<div className='eula-modal__section-title'>Do Not:</div>
				<ul className='eula-modal__paragraph'>
					<li>{ReactHtmlParser(paragraphs[6])}</li>
					<li>{ReactHtmlParser(paragraphs[7])} </li>
					<li>{ReactHtmlParser(paragraphs[8])}</li>
					<li>{ReactHtmlParser(paragraphs[9])}</li>
					<li>{ReactHtmlParser(paragraphs[10])} </li>
					<li>{ReactHtmlParser(paragraphs[11])}</li>
				</ul>

				<div className='eula-modal__section-title underline'>Other Important Terms:</div>

				<div className='eula-modal__paragraph'>{ReactHtmlParser(paragraphs[12])}</div>

				<div className='eula-modal__paragraph'>{ReactHtmlParser(paragraphs[13])}</div>

				<div className='eula-modal__section-title all-caps'>Private and Personal Information</div>

				<div className='eula-modal__paragraph'>{ReactHtmlParser(paragraphs[14])}</div>

				<div className='eula-modal__section-title all-caps'>Additional Guidance</div>
				<div className='eula-modal__paragraph'>{ReactHtmlParser(paragraphs[15])}</div>
				<div className='eula-modal__paragraph'>{ReactHtmlParser(paragraphs[16])}</div>

				<div className='eula-modal__paragraph emphasize'>{ReactHtmlParser(paragraphs[17])}</div>

				<div className='eula-modal__button-container'>
					{!clicked && !openFromMenu && (
						<LoaderButton type='button' disabled={eulaAccepting} loading={eulaAccepting} onHandleClick={handleSend}>
							Accept
						</LoaderButton>
					)}
				</div>
			</Grid>
		</ModalWrapper>
	);
};

export default EulaModal;
